import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import '../styles/CheckoutForm.css';

// Load Stripe
const stripePromise = loadStripe(
  'pk_live_51QKONhLbsPyUh1VvdcyFuXSl0qL4zJzGxEtZTjgjmZjtLS9Icl68rBdwRBKi5gkOWpPF0fKm2fSaU7SEns46xBrf00YudWlhq3'
);

const Checkout = () => {
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPlan, setSelectedPlan] = useState(location.state?.plan || 'pro');

  useEffect(() => {
    // Check if plan was passed in location state
    if (location.state && location.state.plan) {
      setSelectedPlan(location.state.plan);
    } else {
      navigate('/plans', { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    document.title = 'Betalning - TrafikBevakning';
    document.body.style.backgroundColor = '#f5f5f7';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout', plan: selectedPlan } });
      return;
    }
  }, [isAuthenticated, user, token, navigate, selectedPlan]);

  const handleCreateSession = () => {
    createPaymentSession();
  };

  const createPaymentSession = async () => {
    if (!token) return;

    setIsCreatingSession(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: selectedPlan }),
      });

      const data = await response.json();

      if (response.ok) {
        setClientSecret(data.clientSecret);
        setIsCreatingSession(false);
      } else {
        setError(data.error || 'Ett fel uppstod');
        setIsCreatingSession(false);
      }
    } catch (err) {
      console.error('Error fetching client secret:', err);
      setError('Ett oväntat fel uppstod');
      setIsCreatingSession(false);
    }
  };

  const options = {
    clientSecret,
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isCreatingSession) {
    return (
      <div className="checkout-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div className="spinner"></div>
        <p style={{ marginTop: '20px' }}>Förbereder betalning...</p>
      </div>
    );
  }

  const planName =
    selectedPlan === 'basic'
      ? 'Bevakning'
      : selectedPlan === 'pro'
        ? 'Auto-Bokning Pro'
        : 'Auto-Bokning';

  const planPrice = selectedPlan === 'basic' ? '99' : selectedPlan === 'pro' ? '150' : '199';

  if (!clientSecret) {
    return (
      <div
        className="checkout-container"
        style={{ maxWidth: '600px', margin: '40px auto', padding: '0 20px' }}
      >
        <div className="checkout-header" style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 className="checkout-title">Bekräfta din beställning</h1>
          <p className="checkout-subtitle">Granska din prenumeration innan du går vidare</p>
        </div>

        <div style={{ background: '#f5f5f7', padding: '40px', borderRadius: '12px' }}>
          <div className="pricing-info" style={{ marginTop: '0px' }}>
            <div className="price-row">
              <span style={{ fontWeight: '500' }}>{planName} Prenumeration</span>
              <span className="price">{planPrice} kr/månad</span>
            </div>
            <div
              className="price-row total"
              style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}
            >
              <span>Totalt idag</span>
              <span className="price">{planPrice} kr</span>
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ marginTop: '20px' }}>
              {error}
            </div>
          )}

          <button
            onClick={handleCreateSession}
            className="pay-button"
            style={{ marginTop: '30px' }}
            disabled={isCreatingSession}
          >
            {isCreatingSession ? <div className="spinner"></div> : 'Fortsätt till betalning'}
          </button>

          <div className="trust-section">
            <p className="trust-text">🔒 Säker betalning via Stripe</p>
            <p className="trust-text">✓ Avbryt när som helst</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div id="checkout">
        <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );
};

export default Checkout;
